// project-archive.ts — pure rules for the knowledge List's "active vs archived" split + alphabetical sort.
//
// A project is ARCHIVED when it's manually hidden, OR has had no non-private conversation in the last
// STALE_AFTER_DAYS, OR has never had one. PINNED overrides staleness (a pin is "keep this up"), but a
// manual hide always wins. Stale projects return to active automatically the moment new activity lands —
// staleness is derived, never stored. Extracted so the partition + sort are unit-testable.

export const STALE_AFTER_DAYS = 30;
const DAY_MS = 86_400_000;

export interface ProjectActivity {
  hidden?: boolean;
  pinned?: boolean;
  last_activity_at?: string | null;
  name?: string;
}

// No activity, an unparseable timestamp, or activity older than `days` → stale.
export function isStale(
  lastActivityAt: string | null | undefined,
  now: number,
  days = STALE_AFTER_DAYS,
): boolean {
  if (!lastActivityAt) return true;
  const t = Date.parse(lastActivityAt);
  if (Number.isNaN(t)) return true;
  return now - t > days * DAY_MS;
}

export function isProjectArchived(p: ProjectActivity, now: number): boolean {
  if (p.hidden) return true; // manual hide always archives
  if (p.pinned) return false; // pin overrides staleness
  return isStale(p.last_activity_at, now);
}

// Active-set ordering: pinned first, then alphabetical by name (case-insensitive).
export function compareProjectName(a: ProjectActivity, b: ProjectActivity): number {
  const ap = a.pinned ? 1 : 0;
  const bp = b.pinned ? 1 : 0;
  if (ap !== bp) return bp - ap;
  return (a.name ?? '').localeCompare(b.name ?? '');
}
